import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw, Send, ChevronDown, ChevronRight } from "lucide-react";
import ObjectField from "./fields/ObjectField";
import ListField from "./fields/ListField";
import StringField from "./fields/StringField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABELS = {
  hero: "Hero · Above the Fold",
  marquee: "Marquee Strip",
  about: "About · Origin",
  approach: "Approach · Manifesto",
  brutal: "Brutal Design · Featured Project",
  monologue: "Monologue · Showreel",
  gallery: "Gallery",
  contact: "Contact · Initiate Protocol",
  footer: "Footer",
};

function humanise(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function AdminContentTab() {
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [openSection, setOpenSection] = useState("hero");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/content`, { withCredentials: true });
      setDraft(r.data?.draft?.data || null);
      setPublished(r.data?.published?.data || null);
    } catch (e) {
      toast.error("Could not load content");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const dirty = useMemo(() => {
    if (!draft || !published) return false;
    return JSON.stringify(draft) !== JSON.stringify(published);
  }, [draft, published]);

  const handleUploadAsset = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("grade_level", "medium");
    fd.append("watermark", "true");
    try {
      const r = await axios.post(`${API}/admin/media/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded + cinematic graded");
      return r.data?.url;
    } catch (e) {
      toast.error("Upload failed");
      throw e;
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/draft`, { data: draft }, { withCredentials: true });
      toast.success("Draft saved");
      fetchAll();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm("Publish current draft to the live site?")) return;
    setPublishing(true);
    try {
      await axios.put(`${API}/admin/content/draft`, { data: draft }, { withCredentials: true });
      await axios.post(`${API}/admin/content/publish`, {}, { withCredentials: true });
      toast.success("Published to live site");
      fetchAll();
    } catch (e) {
      toast.error("Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm("Discard unpublished draft changes?")) return;
    try {
      await axios.post(`${API}/admin/content/discard`, {}, { withCredentials: true });
      toast.success("Draft discarded");
      fetchAll();
    } catch (e) {
      toast.error("Discard failed");
    }
  };

  if (loading || !draft) {
    return <div className="overline p-10 text-center" data-testid="content-loading">Loading content…</div>;
  }

  const sections = Object.keys(draft);

  return (
    <div data-testid="admin-content-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 sticky top-16 bg-[#050505]/95 z-30 py-4 border-b border-white/10">
        <div>
          <p className="overline">Content Management</p>
          <p className="font-display text-2xl mt-1">
            {dirty ? (
              <span className="text-[#d4a017]" data-testid="dirty-badge">● Unpublished changes</span>
            ) : (
              <span className="text-[#a3a3a3]">○ In sync with live</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="discard-draft-btn"
            onClick={handleDiscard}
            disabled={!dirty}
            className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 px-4 py-2 hover:bg-white hover:text-black transition-all flex items-center gap-2 disabled:opacity-40"
          >
            <RotateCcw size={12} /> Discard
          </button>
          <button
            data-testid="save-draft-btn"
            onClick={handleSaveDraft}
            disabled={saving}
            className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 px-4 py-2 hover:bg-white hover:text-black transition-all flex items-center gap-2 disabled:opacity-40"
          >
            <Save size={12} /> {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            data-testid="publish-btn"
            onClick={handlePublish}
            disabled={publishing}
            className="font-mono text-[10px] tracking-[0.25em] uppercase border border-[#c01515] bg-[#c01515] text-white px-4 py-2 hover:opacity-90 transition-all flex items-center gap-2 disabled:opacity-40"
          >
            <Send size={12} /> {publishing ? "Publishing…" : "Publish to live"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {sections.map((sec) => (
          <Section
            key={sec}
            sec={sec}
            value={draft[sec]}
            isOpen={openSection === sec}
            onToggle={() => setOpenSection(openSection === sec ? null : sec)}
            onChange={(v) => setDraft({ ...draft, [sec]: v })}
            onUploadAsset={handleUploadAsset}
          />
        ))}
      </div>
    </div>
  );
}

function Section({ sec, value, isOpen, onToggle, onChange, onUploadAsset }) {
  return (
    <div className="border border-white/10" data-testid={`section-${sec}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.03]"
        data-testid={`section-toggle-${sec}`}
      >
        <span className="font-display text-lg">{SECTION_LABELS[sec] || humanise(sec)}</span>
        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {isOpen && (
        <div className="p-5 border-t border-white/10">
          <SectionBody value={value} sec={sec} onChange={onChange} onUploadAsset={onUploadAsset} />
        </div>
      )}
    </div>
  );
}

function SectionBody({ value, sec, onChange, onUploadAsset }) {
  if (Array.isArray(value)) {
    return <ListField value={value} onChange={onChange} path={[sec]} onUploadAsset={onUploadAsset} />;
  }
  if (typeof value === "object" && value !== null) {
    return <ObjectField value={value} onChange={onChange} path={[sec]} onUploadAsset={onUploadAsset} />;
  }
  return <StringField value={String(value ?? "")} onChange={onChange} path={[sec]} />;
}
